/**
 * Submission Center — sample dataset.
 *
 * Ported verbatim from the Submission Tracker prototype so the Hub page renders
 * the same book of business the prototype demonstrated. This is SAMPLE DATA:
 * seven opportunities, twenty-seven carrier submissions, and a fixed "today" so
 * every relative date reads consistently. Nothing here comes from the AMS.
 *
 * When the real endpoints land, replace `OPPS` with the API result — everything
 * downstream (KPIs, filters, health, SLA) derives from this shape and needs no
 * other change. The UI shows a "Sample data" marker while this module is the
 * source, so the figures are never mistaken for a live book.
 */

/** Fixed reference date the sample data is written against: Mon 10 Aug 2026. */
export const TODAY = new Date(2026, 7, 10);

export interface Named { name: string; email: string; phone: string; }
export interface Subjectivity { label: string; status: 'open' | 'cleared'; on?: string; due?: string; owner?: string; }
export interface SubFlag { kind: 'sla' | 'coverage' | 'critical' | 'client' | 'expiry'; label: string; }
export interface SubDoc { name: string; kind: string; ok: boolean; note: string; when: string; file?: File; }
export interface Comm { who: string; dir: 'in' | 'out' | 'sys'; channel: string; txt: string; when: string; }

export interface Submission {
  id: string; carrier: string; market: string; channel: string; submissionNo: string;
  lob: string; stage: string; submittedOn: string | null; lastActivity: string; slaDays: number;
  premium: number | null; commission: number | null; limits: string; deductible: string;
  validUntil: string | null; incumbent?: boolean; declineReason?: string;
  /** The MGA/wholesaler placing this quote, when the market was reached that way — distinct
   *  from `carrier`, which is the underlying insurance company. */
  mga?: string | null;
  /** The quote's own policy term — separate from `validUntil`, which is how long the quote itself is good for. */
  effectiveDate?: string | null; expDate?: string | null;
  /** Freeform note captured when the quote was added. */
  note?: string;
  uw: Named; subjectivities: Subjectivity[]; flags: SubFlag[]; docs: SubDoc[]; comms: Comm[];
}

export interface ClientDoc { t: string; s: string; status: 'open' | 'done' | 'late'; }
export interface ClientMsg { who: string; txt: string; when: string; }
export interface Automation { t: string; s: string; on: boolean; fired: string; }
export interface NextAction { label: string; owner: string; due: string; done?: boolean; }

export interface Opportunity {
  id: string; account: string; dba?: string | null; oppName: string;
  lobs: string[]; lobShort: string; type: string; oppStage: string; tier: string;
  effective: string; expiring?: string | null; expiringPremium?: number | null; targetPremium: number;
  producer: string; csr: string; marketer: string;
  contact: { name: string; title: string; email: string; phone: string };
  quoteRef: string; health: 'on' | 'watch' | 'risk';
  tracker: { shared: boolean; views: number; lastView: string; proposalSent?: string; proposalOpened?: boolean };
  nextAction: NextAction; note: string;
  submissions: Submission[]; clientDocs: ClientDoc[]; clientMsgs: ClientMsg[];
  automations: Automation[];
}

export interface StageDef { id: string; label: string; short: string; color: string; tint: string; desc: string; }
export interface OppStageDef { id: string; label: string; color: string; }

export const SUB_STAGES: StageDef[] = [
  {id:'prep',      label:'In prep',        short:'Prep',      color:'#9AA0A6', tint:'#F2F3F5', desc:'Packaging the submission'},
  {id:'submitted', label:'Submitted',      short:'Submitted', color:'#6B7684', tint:'#EDEFF2', desc:'Awaiting carrier acknowledgement'},
  {id:'review',    label:'Carrier review', short:'Review',    color:'#5A7FE0', tint:'#E9EEFC', desc:'In underwriting'},
  {id:'info',      label:'Info requested', short:'Info req',  color:'#C9821E', tint:'#FCF3E2', desc:'Blocked on us or the client'},
  {id:'quoted',    label:'Quoted',         short:'Quoted',    color:'#00B0D3', tint:'#E4F5FA', desc:'Terms in hand'},
  {id:'bound',     label:'Bound',          short:'Bound',     color:'#2E9E6D', tint:'#E6F5EE', desc:'Placed and confirmed'},
  {id:'declined',  label:'Declined',       short:'Declined',  color:'#D64545', tint:'#FBEAEA', desc:'No appetite / no offer'},
  {id:'ntu',       label:'Not taken up',   short:'NTU',       color:'#8A8F98', tint:'#F1F2F4', desc:'Quoted, lost to another market'},
];
export const S: Record<string, StageDef> =
  SUB_STAGES.reduce((acc, s) => { acc[s.id] = s; return acc; }, {} as Record<string, StageDef>);
export const OPEN_STAGES = ['prep','submitted','review','info','quoted'];
export const CLOSED_STAGES = ['bound','declined','ntu'];
export const FUNNEL_ORDER = ['prep','submitted','review','info','quoted','bound','declined','ntu'];

export const OPP_STAGES: OppStageDef[] = [
  {id:'marketing',  label:'Marketing',      color:'#6B7684'},
  {id:'quotes_in',  label:'Quotes received',color:'#00B0D3'},
  {id:'proposal',   label:'Proposal sent',  color:'#A200FF'},
  {id:'bound',      label:'Bound / Won',    color:'#2E9E6D'},
  {id:'lost',       label:'Lost',           color:'#D64545'},
];
export const OS: Record<string, OppStageDef> =
  OPP_STAGES.reduce((acc, s) => { acc[s.id] = s; return acc; }, {} as Record<string, OppStageDef>);

export const HEALTH: Record<string, { label: string; color: string; tint: string; deep: string }> = {
  on:   {label:'On track', color:'#17795E', tint:'#E9F4F0', deep:'#12604B'},
  watch:{label:'Watch',    color:'#8A5A17', tint:'#FAF3E6', deep:'#6E4712'},
  risk: {label:'At risk',  color:'#B4322C', tint:'#FBECEB', deep:'#8C1D18'},
};

export const CLIENT_STEPS = [
  {k:'submitted', l:'Submitted to market'},
  {k:'review',    l:'Carriers reviewing'},
  {k:'options',   l:'Options ready'},
  {k:'proposal',  l:'Proposal sent'},
  {k:'bound',     l:'Coverage bound'},
];

export const OPPS: Opportunity[] = [
{
  id:'cadence', account:'Cadence Defense Systems', dba:'Cadence Defense',
  oppName:'GL + Property — 2026 Renewal', lobs:['General Liability','Commercial Property'],
  lobShort:'GL', type:'Renewal', oppStage:'quotes_in', tier:'Level 3',
  effective:'2026-09-01', expiring:'2026-09-01', expiringPremium:184200, targetPremium:178000,
  producer:'Rachel Vance', csr:'Priya Shah', marketer:'Devon Cole',
  contact:{name:'Marcus Webb', title:'CFO', email:'mwebb@cadencedefense.com', phone:'(555) 214-8890'},
  quoteRef:'QT-CD-4471', health:'watch',
  tracker:{shared:true, views:7, lastView:'2026-08-08'},
  nextAction:{label:'Chase Coterie — 7 days with no acknowledgement', owner:'Priya Shah', due:'2026-08-10'},
  note:'Incumbent is Travelers. Client flagged the site-inspection subjectivity as a timing risk — schedule before Aug 18 or the quote lapses.',
  submissions:[
    {id:'cd-1', carrier:'Travelers', market:'Admitted', channel:'Direct appointment', submissionNo:'SUB-882301',
     lob:'General Liability', stage:'quoted', submittedOn:'2026-07-22', lastActivity:'2026-08-09', slaDays:5,
     premium:171450, commission:12.5, limits:'$1M / $2M', deductible:'$5,000', validUntil:'2026-09-05', incumbent:true,
     uw:{name:'Alicia Trent', email:'atrent@travelers.com', phone:'(860) 277-1041'},
     subjectivities:[
       {label:'Signed application', status:'cleared', on:'2026-07-23'},
       {label:'3-year GL loss runs', status:'cleared', on:'2026-07-29'},
       {label:'On-site risk inspection', status:'open', due:'2026-08-18', owner:'Priya Shah'}],
     flags:[],
     docs:[{name:'Travelers_Quote_GL_2026.pdf', kind:'Quote', ok:true, note:'Reviewed — no restrictive exclusions detected', when:'2026-08-09'},
           {name:'Travelers_Binder_Draft.pdf', kind:'Binder', ok:true, note:'Draft, unsigned', when:'2026-08-09'}],
     comms:[
       {who:'Alicia Trent · Travelers', dir:'in', channel:'Email', txt:'Quote issued at $171,450. Valid 30 days. Inspection must be completed before binding.', when:'2026-08-09'},
       {who:'Priya Shah', dir:'out', channel:'Email', txt:'Sent updated 3-year loss runs and confirmed payroll figures.', when:'2026-07-29'},
       {who:'Momentum Automation', dir:'sys', channel:'Automation', txt:'Follow-up sent at day 5 — no response yet from carrier.', when:'2026-07-27'}]},

    {id:'cd-2', carrier:'Coterie', market:'Admitted', channel:'via Rate', submissionNo:'SUB-882344',
     lob:'General Liability', stage:'submitted', submittedOn:'2026-08-03', lastActivity:'2026-08-03', slaDays:5,
     premium:null, commission:15, limits:'$1M / $2M', deductible:'—', validUntil:null,
     uw:{name:'Unassigned', email:'submissions@coterieins.com', phone:'(855) 254-0220'},
     subjectivities:[{label:'Signed application', status:'cleared', on:'2026-08-03'}],
     flags:[{kind:'sla', label:'No carrier response · 7 days'}],
     docs:[{name:'Coterie_Application.pdf', kind:'Application', ok:true, note:'Submitted via Rate', when:'2026-08-03'}],
     comms:[{who:'Priya Shah', dir:'out', channel:'Portal', txt:'Submitted GL application with supplemental armed-services questionnaire.', when:'2026-08-03'},
            {who:'Momentum Automation', dir:'sys', channel:'Automation', txt:'Day-5 chase queued — will send today at 4:00 PM unless cancelled.', when:'2026-08-08'}]},

    {id:'cd-3', carrier:'USLI', market:'E&S / surplus lines', channel:'Direct appointment', submissionNo:'SUB-882309',
     lob:'General Liability', stage:'declined', submittedOn:'2026-07-24', lastActivity:'2026-08-05', slaDays:5,
     premium:null, commission:null, limits:'—', deductible:'—', validUntil:null,
     declineReason:'Class outside current appetite — armed security operations.',
     uw:{name:'Jordan Pike', email:'jpike@usli.com', phone:'(888) 523-5545'},
     subjectivities:[], flags:[], docs:[],
     comms:[{who:'Jordan Pike · USLI', dir:'in', channel:'Email', txt:'Declining — armed security operations fall outside our GL appetite this year.', when:'2026-08-05'}]},

    {id:'cd-4', carrier:'Employers Mutual', market:'Admitted', channel:'Direct appointment', submissionNo:'SUB-882361',
     lob:'General Liability', stage:'review', submittedOn:'2026-07-28', lastActivity:'2026-08-06', slaDays:7,
     premium:null, commission:12, limits:'$1M / $2M', deductible:'$10,000', validUntil:null,
     uw:{name:'Sam Okafor', email:'sokafor@employers.com', phone:'(775) 887-2100'},
     subjectivities:[{label:'Signed application', status:'cleared', on:'2026-07-28'},
                     {label:'Contracts / hold-harmless samples', status:'cleared', on:'2026-08-06'}],
     flags:[],
     docs:[{name:'Employers_Ack_Receipt.pdf', kind:'Acknowledgement', ok:true, note:'Assigned to underwriting', when:'2026-07-30'}],
     comms:[{who:'Sam Okafor · Employers', dir:'in', channel:'Email', txt:'Received contract samples — targeting an indication by Aug 12.', when:'2026-08-06'}]},

    {id:'cd-5', carrier:'Markel', market:'E&S / surplus lines', channel:'Wholesale / MGA', submissionNo:'SUB-882377',
     lob:'Commercial Property', stage:'info', submittedOn:'2026-07-30', lastActivity:'2026-08-07', slaDays:5,
     premium:null, commission:15, limits:'$4.2M blanket', deductible:'$25,000', validUntil:null,
     uw:{name:'Nia Bertrand', email:'nbertrand@markel.com', phone:'(804) 747-0136'},
     subjectivities:[{label:'2025 payroll audit', status:'open', due:'2026-08-12', owner:'Marcus Webb (client)'},
                     {label:'Roof age documentation', status:'open', due:'2026-08-12', owner:'Marcus Webb (client)'},
                     {label:'Signed application', status:'cleared', on:'2026-07-30'}],
     flags:[{kind:'client', label:'Waiting on client · 2 items'}],
     docs:[{name:'Markel_Info_Request.pdf', kind:'Info request', ok:true, note:'2 outstanding items', when:'2026-08-07'}],
     comms:[{who:'Nia Bertrand · Markel', dir:'in', channel:'Email', txt:'Need 2025 payroll audit and roof age before we can release terms.', when:'2026-08-07'},
            {who:'Momentum Automation', dir:'sys', channel:'Automation', txt:'Document request sent to Marcus Webb via the client tracker.', when:'2026-08-07'}]},

    {id:'cd-6', carrier:'Hiscox', market:'Admitted', channel:'via Rate', submissionNo:'SUB-882390',
     lob:'General Liability', stage:'quoted', submittedOn:'2026-07-25', lastActivity:'2026-08-07', slaDays:5,
     premium:189300, commission:12, limits:'$1M / $2M', deductible:'$5,000', validUntil:'2026-09-07',
     uw:{name:'Grace Lin', email:'glin@hiscox.com', phone:'(646) 452-2353'},
     subjectivities:[{label:'Signed application', status:'cleared', on:'2026-07-25'}],
     flags:[{kind:'coverage', label:'Professional services exclusion'}],
     docs:[{name:'Hiscox_Quote_GL.pdf', kind:'Quote', ok:false, note:'Professional services exclusion detected — narrows consulting revenue', when:'2026-08-07'}],
     comms:[{who:'Grace Lin · Hiscox', dir:'in', channel:'Email', txt:'Terms attached at $189,300. Note the professional services exclusion on form HGL-204.', when:'2026-08-07'}]},
  ],
  automations:[
    {t:'Carrier follow-up at day 5', s:'Emails the underwriter and logs the touch to the submission', on:true, fired:'Fired 3× · last Aug 8'},
    {t:'Client document chase', s:'Requests outstanding subjectivity items through the tracker', on:true, fired:'Fired 2× · last Aug 7'},
    {t:'Quote expiry warning at 7 days', s:'Alerts producer + CSR before terms lapse', on:true, fired:'Next fires Aug 29'},
    {t:'Stage-change client email', s:'Notifies the insured whenever the tracker advances a step', on:false, fired:'Off — producer sends manually'},
  ],
  clientDocs:[
    {t:'2025 payroll audit', s:'Requested Aug 7 · needed for property terms', status:'open'},
    {t:'Roof age documentation', s:'Requested Aug 7 · needed for property terms', status:'open'},
    {t:'Signed application', s:'Received Jul 23', status:'done'},
    {t:'3-year loss runs', s:'Received Jul 29', status:'done'},
  ],
  clientMsgs:[
    {who:'Priya Shah · Sunrise Insurance', txt:'Two quotes are in and two more markets are still reviewing. Once we have the payroll audit and roof documentation we can release the property terms.', when:'2026-08-08'},
    {who:'Marcus Webb', txt:'Payroll audit should be ready Wednesday. Roof docs are with our facilities manager.', when:'2026-08-08'},
  ],
},

{
  // Same account as 'cadence' above, shopped at the same time — a second,
  // independent opportunity rather than another line inside the first one.
  // Commercial accounts routinely run more than one opportunity in parallel
  // (e.g. a renewal plus a new-business line), so the tracker groups
  // opportunities sharing an account under one shared header.
  id:'cadence-cyber', account:'Cadence Defense Systems', dba:'Cadence Defense',
  oppName:'Cyber — New Business', lobs:['Cyber'],
  lobShort:'CYB', type:'New Business', oppStage:'marketing', tier:'Level 3',
  effective:'2026-09-01', expiring:null, expiringPremium:null, targetPremium:14500,
  producer:'Rachel Vance', csr:'Priya Shah', marketer:'Devon Cole',
  contact:{name:'Marcus Webb', title:'CFO', email:'mwebb@cadencedefense.com', phone:'(555) 214-8890'},
  quoteRef:'QT-CD-4502', health:'on',
  tracker:{shared:false, views:0, lastView:'2026-08-05'},
  nextAction:{label:'Confirm security questionnaire answers with Marcus before quoting', owner:'Priya Shah', due:'2026-08-13'},
  note:'Bundling with the GL/Property renewal at Marcus’s request — first cyber policy for this account.',
  submissions:[
    {id:'cdc-1', carrier:'Coalition', market:'Admitted', channel:'via Rate', submissionNo:'SUB-882410',
     lob:'Cyber', stage:'submitted', submittedOn:'2026-08-05', lastActivity:'2026-08-05', slaDays:5,
     premium:null, commission:15, limits:'$1M aggregate', deductible:'$10,000', validUntil:null,
     uw:{name:'Ivy Nakamura', email:'inakamura@coalitioninc.com', phone:'(415) 855-4700'},
     subjectivities:[{label:'Security questionnaire', status:'open', due:'2026-08-13', owner:'Marcus Webb (client)'}],
     flags:[],
     docs:[{name:'Coalition_Cyber_Application.pdf', kind:'Application', ok:true, note:'Submitted via Rate', when:'2026-08-05'}],
     comms:[{who:'Priya Shah', dir:'out', channel:'Portal', txt:'Submitted the cyber application alongside the GL/Property renewal.', when:'2026-08-05'}]},

    {id:'cdc-2', carrier:'Beazley', market:'Admitted', channel:'Direct appointment', submissionNo:'SUB-882417',
     lob:'Cyber', stage:'prep', submittedOn:null, lastActivity:'2026-08-06', slaDays:0,
     premium:null, commission:15, limits:'$1M aggregate', deductible:'$15,000', validUntil:null,
     uw:{name:'Not yet assigned', email:'—', phone:'—'},
     subjectivities:[],
     flags:[],
     docs:[],
     comms:[{who:'Priya Shah', dir:'out', channel:'Automation', txt:'Held as a second market pending the security questionnaire.', when:'2026-08-06'}]},
  ],
  automations:[
    {t:'Carrier follow-up at day 5', s:'Emails the underwriter and logs the touch to the submission', on:true, fired:'Next fires Aug 10'},
    {t:'Client document chase', s:'Requests outstanding subjectivity items through the tracker', on:true, fired:'Fired 1× · last Aug 5'},
  ],
  clientDocs:[
    {t:'Security questionnaire', s:'Requested Aug 5 · required by all markets', status:'open'},
  ],
  clientMsgs:[
    {who:'Priya Shah · Sunrise Insurance', txt:'Starting the cyber quote alongside your renewal — just need the security questionnaire back to get terms moving.', when:'2026-08-05'},
  ],
},

{
  id:'emerald', account:'Emerald Bay Holdings', dba:'Emerald Bay',
  oppName:'BOP / Package — New Business', lobs:['Businessowners Package'],
  lobShort:'BOP', type:'New Business', oppStage:'quotes_in', tier:'Level 2',
  effective:'2026-08-15', expiring:null, expiringPremium:null, targetPremium:96500,
  producer:'Rachel Vance', csr:'Marcus Lin', marketer:'Devon Cole',
  contact:{name:'Sonia Alvarez', title:'Managing Partner', email:'salvarez@emeraldbayhold.com', phone:'(555) 331-7742'},
  quoteRef:'QT-EB-2290', health:'risk',
  tracker:{shared:true, views:12, lastView:'2026-08-09'},
  nextAction:{label:'Review A&B exclusion with client, then build proposal', owner:'Rachel Vance', due:'2026-08-10'},
  note:'Effective date is 5 days out. First Connect is cheapest on paper but carries an Assault & Battery exclusion — a material gap for a mixed-use property with a tenant bar.',
  submissions:[
    {id:'eb-1', carrier:'Travelers', market:'Admitted', channel:'Direct appointment', submissionNo:'SUB-884120',
     lob:'Businessowners Package', stage:'quoted', submittedOn:'2026-07-27', lastActivity:'2026-08-08', slaDays:5,
     premium:92340, commission:13, limits:'$2M GL / $6.4M bldg', deductible:'$5,000', validUntil:'2026-08-20',
     uw:{name:'Dev Raman', email:'draman@travelers.com', phone:'(860) 277-4180'},
     subjectivities:[{label:'Signed application', status:'cleared', on:'2026-07-27'},
                     {label:'Property inspection', status:'open', due:'2026-08-13', owner:'Marcus Lin'}],
     flags:[{kind:'expiry', label:'Quote expires in 10 days'}],
     docs:[{name:'Travelers_BOP_Quote.pdf', kind:'Quote', ok:true, note:'A&B coverage included at $1M sublimit', when:'2026-08-08'}],
     comms:[{who:'Dev Raman · Travelers', dir:'in', channel:'Email', txt:'Quote at $92,340 including A&B at a $1M sublimit. Inspection ordered for Aug 13.', when:'2026-08-08'},
            {who:'Marcus Lin', dir:'out', channel:'Phone', txt:'Confirmed tenant mix and hours of operation for the ground-floor bar.', when:'2026-08-04'}]},

    {id:'eb-2', carrier:'First Connect', market:'Wholesale / MGA', channel:'Wholesale / MGA', submissionNo:'SUB-884133',
     lob:'Businessowners Package', stage:'quoted', submittedOn:'2026-07-29', lastActivity:'2026-08-09', slaDays:5,
     premium:88750, commission:15, limits:'$2M GL / $6.4M bldg', deductible:'$10,000', validUntil:'2026-08-22',
     uw:{name:'Tomas Reyes', email:'treyes@firstconnect.com', phone:'(877) 803-3013'},
     subjectivities:[{label:'Signed application', status:'cleared', on:'2026-07-29'},
                     {label:'Confirm building construction type', status:'open', due:'2026-08-11', owner:'Marcus Lin'}],
     flags:[{kind:'critical', label:'Assault & Battery exclusion'}],
     docs:[{name:'FirstConnect_Quote_v2.pdf', kind:'Quote', ok:false, note:'CRITICAL — Assault & Battery exclusion on a property with a tenant bar', when:'2026-08-09'}],
     comms:[{who:'Tomas Reyes · First Connect', dir:'in', channel:'Email', txt:'Revised quote attached at $88,750. Confirm construction type to lock terms.', when:'2026-08-09'},
            {who:'Momentum Automation', dir:'sys', channel:'Automation', txt:'Coverage scan flagged an Assault & Battery exclusion — escalated to producer.', when:'2026-08-09'}]},

    {id:'eb-3', carrier:'Nationwide', market:'Admitted', channel:'Direct appointment', submissionNo:'SUB-884101',
     lob:'Businessowners Package', stage:'declined', submittedOn:'2026-07-26', lastActivity:'2026-08-04', slaDays:5,
     premium:null, commission:null, limits:'—', deductible:'—', validUntil:null,
     declineReason:'Habitational exposure exceeds 40% of total insured value.',
     uw:{name:'Beth Cardoza', email:'cardozb@nationwide.com', phone:'(614) 249-7111'},
     subjectivities:[], flags:[], docs:[],
     comms:[{who:'Beth Cardoza · Nationwide', dir:'in', channel:'Email', txt:'Habitational exposure is above our 40% threshold — must decline.', when:'2026-08-04'}]},

    {id:'eb-4', carrier:'Coterie', market:'Admitted', channel:'via Rate', submissionNo:'SUB-884147',
     lob:'Businessowners Package', stage:'info', submittedOn:'2026-08-01', lastActivity:'2026-08-06', slaDays:5,
     premium:null, commission:15, limits:'—', deductible:'—', validUntil:null,
     uw:{name:'Automated underwriting', email:'submissions@coterieins.com', phone:'(855) 254-0220'},
     subjectivities:[{label:'Sprinkler certification', status:'open', due:'2026-08-11', owner:'Sonia Alvarez (client)'}],
     flags:[{kind:'client', label:'Waiting on client · 1 item'}],
     docs:[], comms:[{who:'Coterie', dir:'in', channel:'Portal', txt:'Sprinkler certification required for the retail occupancy before terms release.', when:'2026-08-06'}]},
  ],
  automations:[
    {t:'Carrier follow-up at day 5', s:'Emails the underwriter and logs the touch', on:true, fired:'Fired 2× · last Aug 6'},
    {t:'Coverage-gap scan on every quote PDF', s:'Flags exclusions the agency has blacklisted', on:true, fired:'Flagged 1 critical · Aug 9'},
    {t:'Quote expiry warning at 7 days', s:'Two quotes lapse inside the next 12 days', on:true, fired:'Fires Aug 13'},
    {t:'Stage-change client email', s:'Notifies the insured on every tracker step', on:true, fired:'Fired 4× · last Aug 9'},
  ],
  clientDocs:[
    {t:'Sprinkler certification', s:'Requested Aug 6 · required by one carrier', status:'open'},
    {t:'Building construction confirmation', s:'Requested Aug 9', status:'open'},
    {t:'Signed application', s:'Received Jul 26', status:'done'},
  ],
  clientMsgs:[
    {who:'Rachel Vance · Sunrise Insurance', txt:'We have two firm options. One is about $3,600 cheaper but excludes assault & battery — with a bar on the ground floor I want to walk you through that before you decide.', when:'2026-08-09'},
    {who:'Sonia Alvarez', txt:'Understood — can we talk Monday morning? Coverage matters more than the difference.', when:'2026-08-09'},
  ],
},

{
  id:'banks', account:'Banks Brokerage LLC', dba:'Banks Brokerage',
  oppName:'Commercial Auto — Fleet Renewal', lobs:['Commercial Auto'],
  lobShort:'CA', type:'Renewal', oppStage:'quotes_in', tier:'Level 3',
  effective:'2026-10-01', expiring:'2026-10-01', expiringPremium:218900, targetPremium:212000,
  producer:'Tomas Ibarra', csr:'Dana Ruiz', marketer:'Devon Cole',
  contact:{name:'Leah Banks', title:'Owner', email:'leah@banksbrokerage.com', phone:'(555) 448-1120'},
  quoteRef:'QT-BB-3167', health:'on',
  tracker:{shared:true, views:3, lastView:'2026-08-05'},
  nextAction:{label:'Order MVRs for 2 newly hired drivers', owner:'Dana Ruiz', due:'2026-08-14'},
  note:'18-unit fleet, clean 3-year loss history. Nationwide came in first with a fleet-safety credit applied.',
  submissions:[
    {id:'bb-1', carrier:'Nationwide', market:'Admitted', channel:'Direct appointment', submissionNo:'SUB-886002',
     lob:'Commercial Auto', stage:'quoted', submittedOn:'2026-07-31', lastActivity:'2026-08-09', slaDays:7,
     premium:211400, commission:12, limits:'$1M CSL', deductible:'$1,000 comp / $1,000 coll', validUntil:'2026-10-05',
     uw:{name:'Ken Whitmore', email:'whitmok@nationwide.com', phone:'(614) 249-8802'},
     subjectivities:[{label:'Signed application', status:'cleared', on:'2026-07-31'},
                     {label:'MVRs — all drivers', status:'open', due:'2026-08-14', owner:'Dana Ruiz'}],
     flags:[],
     docs:[{name:'Nationwide_Fleet_Quote.pdf', kind:'Quote', ok:true, note:'Fleet safety credit applied — 6.5%', when:'2026-08-09'}],
     comms:[{who:'Ken Whitmore · Nationwide', dir:'in', channel:'Email', txt:'Quote issued at $211,400 with the fleet safety credit. MVRs needed for the 2 new drivers.', when:'2026-08-09'}]},

    {id:'bb-2', carrier:'Progressive Commercial', market:'Admitted', channel:'via Rate', submissionNo:'SUB-886014',
     lob:'Commercial Auto', stage:'review', submittedOn:'2026-08-04', lastActivity:'2026-08-07', slaDays:7,
     premium:null, commission:11, limits:'$1M CSL', deductible:'—', validUntil:null,
     uw:{name:'Fleet desk', email:'fleetdesk@progressive.com', phone:'(800) 444-4487'},
     subjectivities:[{label:'Signed application', status:'cleared', on:'2026-08-04'}],
     flags:[], docs:[],
     comms:[{who:'Progressive fleet desk', dir:'in', channel:'Portal', txt:'Submission in underwriting review — indication expected within 5 business days.', when:'2026-08-07'}]},

    {id:'bb-3', carrier:'The Hartford', market:'Admitted', channel:'Direct appointment', submissionNo:'SUB-886021',
     lob:'Commercial Auto', stage:'submitted', submittedOn:'2026-08-06', lastActivity:'2026-08-06', slaDays:7,
     premium:null, commission:12, limits:'$1M CSL', deductible:'—', validUntil:null,
     uw:{name:'Renee Vogel', email:'renee.vogel@thehartford.com', phone:'(860) 547-5000'},
     subjectivities:[{label:'Signed application', status:'cleared', on:'2026-08-06'}],
     flags:[], docs:[],
     comms:[{who:'Dana Ruiz', dir:'out', channel:'Email', txt:'Submitted with 3-year loss runs, driver schedule and vehicle list.', when:'2026-08-06'}]},

    {id:'bb-4', carrier:'Berkshire GUARD', market:'Admitted', channel:'Direct appointment', submissionNo:'—',
     lob:'Commercial Auto', stage:'prep', submittedOn:null, lastActivity:'2026-08-09', slaDays:0,
     premium:null, commission:12, limits:'—', deductible:'—', validUntil:null,
     uw:{name:'Not yet assigned', email:'—', phone:'—'},
     subjectivities:[{label:'Driver schedule with CDL dates', status:'open', due:'2026-08-13', owner:'Dana Ruiz'}],
     flags:[], docs:[],
     comms:[{who:'Dana Ruiz', dir:'out', channel:'Automation', txt:'Held as a fourth market — release only if Progressive or Hartford decline.', when:'2026-08-09'}]},
  ],
  automations:[
    {t:'Carrier follow-up at day 7', s:'Fleet markets run a longer SLA than monoline GL', on:true, fired:'Next fires Aug 13'},
    {t:'MVR order reminder', s:'Reminds the CSR 3 days before the subjectivity due date', on:true, fired:'Fires Aug 11'},
    {t:'Stage-change client email', s:'Notifies the insured on every tracker step', on:true, fired:'Fired 3× · last Aug 9'},
  ],
  clientDocs:[
    {t:'Driver list — 2 new hires', s:'Requested Aug 5', status:'open'},
    {t:'Vehicle schedule', s:'Received Aug 3', status:'done'},
    {t:'3-year loss runs', s:'Received Jul 30', status:'done'},
  ],
  clientMsgs:[
    {who:'Dana Ruiz · Sunrise Insurance', txt:'First quote is in and it is tracking below your expiring premium. Two more markets are still working. Send over the two new drivers when you can.', when:'2026-08-09'},
  ],
},

{
  id:'harborline', account:'Harborline Logistics', dba:'Harborline',
  oppName:'Trucking + Motor Truck Cargo — New Business', lobs:['Commercial Auto','Inland Marine'],
  lobShort:'TRK', type:'New Business', oppStage:'marketing', tier:'Level 2',
  effective:'2026-09-15', expiring:null, expiringPremium:null, targetPremium:312000,
  producer:'Tomas Ibarra', csr:'Dana Ruiz', marketer:'Devon Cole',
  contact:{name:'Ray Okonkwo', title:'Operations Director', email:'rokonkwo@harborline.co', phone:'(555) 662-9014'},
  quoteRef:'QT-HL-5508', health:'watch',
  tracker:{shared:true, views:1, lastView:'2026-08-02'},
  nextAction:{label:'Chase client for 3-year loss runs — 2nd request', owner:'Dana Ruiz', due:'2026-08-08'},
  note:'Nothing can be quoted until the loss runs land. Two markets are already holding for them and the effective date is 36 days out.',
  submissions:[
    {id:'hl-1', carrier:'Great West Casualty', market:'Admitted', channel:'Direct appointment', submissionNo:'SUB-887301',
     lob:'Commercial Auto', stage:'info', submittedOn:'2026-07-30', lastActivity:'2026-08-06', slaDays:5,
     premium:null, commission:10, limits:'$1M CSL', deductible:'—', validUntil:null,
     uw:{name:'Carla Denton', email:'cdenton@gwccnet.com', phone:'(800) 228-8602'},
     subjectivities:[{label:'3-year loss runs', status:'open', due:'2026-08-12', owner:'Ray Okonkwo (client)'},
                     {label:'IFTA filings — 4 quarters', status:'open', due:'2026-08-12', owner:'Ray Okonkwo (client)'}],
     flags:[{kind:'client', label:'Waiting on client · 2 items'}],
     docs:[], comms:[{who:'Carla Denton · Great West', dir:'in', channel:'Email', txt:'Cannot release an indication without loss runs and IFTA filings.', when:'2026-08-06'}]},

    {id:'hl-2', carrier:'Protective Insurance', market:'E&S / surplus lines', channel:'Wholesale / MGA', submissionNo:'SUB-887318',
     lob:'Commercial Auto', stage:'submitted', submittedOn:'2026-08-05', lastActivity:'2026-08-05', slaDays:5,
     premium:null, commission:15, limits:'$1M CSL', deductible:'—', validUntil:null,
     uw:{name:'Wholesale desk', email:'desk@protectiveins.com', phone:'(800) 644-5501'},
     subjectivities:[{label:'Signed application', status:'cleared', on:'2026-08-05'}],
     flags:[], docs:[],
     comms:[{who:'Dana Ruiz', dir:'out', channel:'Email', txt:'Submitted with fleet list, radius profile and driver roster. Loss runs to follow.', when:'2026-08-05'}]},

    {id:'hl-3', carrier:'Canal Insurance', market:'E&S / surplus lines', channel:'Wholesale / MGA', submissionNo:'SUB-887309',
     lob:'Commercial Auto', stage:'declined', submittedOn:'2026-08-01', lastActivity:'2026-08-07', slaDays:5,
     premium:null, commission:null, limits:'—', deductible:'—', validUntil:null,
     declineReason:'Operating radius above 500 miles on a venture under 3 years old.',
     uw:{name:'Miles Arroyo', email:'marroyo@canalins.com', phone:'(864) 585-6161'},
     subjectivities:[], flags:[], docs:[],
     comms:[{who:'Miles Arroyo · Canal', dir:'in', channel:'Email', txt:'Long-haul radius plus new-venture status puts this outside appetite.', when:'2026-08-07'}]},

    {id:'hl-4', carrier:'Lancer Insurance', market:'E&S / surplus lines', channel:'Wholesale / MGA', submissionNo:'—',
     lob:'Inland Marine', stage:'prep', submittedOn:null, lastActivity:'2026-08-09', slaDays:0,
     premium:null, commission:15, limits:'$250K cargo', deductible:'—', validUntil:null,
     uw:{name:'Not yet assigned', email:'—', phone:'—'},
     subjectivities:[{label:'Cargo schedule and commodity list', status:'open', due:'2026-08-14', owner:'Dana Ruiz'}],
     flags:[], docs:[],
     comms:[{who:'Dana Ruiz', dir:'out', channel:'Automation', txt:'Cargo market staged — release with the auto submission once loss runs arrive.', when:'2026-08-09'}]},
  ],
  automations:[
    {t:'Client document chase', s:'Escalates to a phone-task after two unanswered requests', on:true, fired:'Escalated Aug 8 → task created'},
    {t:'Carrier hold notice', s:'Tells holding markets we are still gathering data', on:true, fired:'Fired 1× · Aug 6'},
    {t:'Stage-change client email', s:'Notifies the insured on every tracker step', on:true, fired:'Fired 1× · Aug 2'},
  ],
  clientDocs:[
    {t:'3-year loss runs', s:'Requested Jul 30, again Aug 5 — still outstanding', status:'late'},
    {t:'IFTA filings — 4 quarters', s:'Requested Jul 30 — still outstanding', status:'late'},
    {t:'Signed application', s:'Received Jul 29', status:'done'},
  ],
  clientMsgs:[
    {who:'Dana Ruiz · Sunrise Insurance', txt:'Two carriers are holding your file but neither can quote without the loss runs from your prior broker. Want me to request them on your behalf?', when:'2026-08-08'},
  ],
},
{
  id:'meridian', account:'Meridian Dental Group', dba:'Meridian Dental',
  oppName:'Workers Comp + Cyber — 2026 Renewal', lobs:['Workers Compensation','Cyber'],
  lobShort:'WC', type:'Renewal', oppStage:'bound', tier:'Level 3',
  effective:'2026-11-01', expiring:'2026-11-01', expiringPremium:46800, targetPremium:45000,
  producer:'Rachel Vance', csr:'Priya Shah', marketer:'Devon Cole',
  contact:{name:'Dr. Amara Osei', title:'Practice Owner', email:'aosei@meridiandental.com', phone:'(555) 770-3312'},
  quoteRef:'QT-MD-1904', health:'on',
  tracker:{shared:true, views:9, lastView:'2026-08-07'},
  nextAction:{label:'Issue certificates and deliver policy documents', owner:'Priya Shah', due:'2026-08-07', done:true},
  note:'Bound 5 days early. Cyber written separately with Coalition — combined premium came in $4,170 under expiring.',
  submissions:[
    {id:'md-1', carrier:'Employers', market:'Admitted', channel:'Direct appointment', submissionNo:'SUB-881002',
     lob:'Workers Compensation', stage:'bound', submittedOn:'2026-07-10', lastActivity:'2026-08-06', slaDays:5,
     premium:35780, commission:10, limits:'Statutory / $1M EL', deductible:'—', validUntil:'2026-11-01', incumbent:true,
     uw:{name:'Sam Okafor', email:'sokafor@employers.com', phone:'(775) 887-2100'},
     subjectivities:[{label:'Signed application', status:'cleared', on:'2026-07-10'},
                     {label:'Payroll verification', status:'cleared', on:'2026-07-24'},
                     {label:'Experience mod worksheet', status:'cleared', on:'2026-07-28'}],
     flags:[],
     docs:[{name:'Employers_WC_Binder.pdf', kind:'Binder', ok:true, note:'Signed and countersigned', when:'2026-08-06'},
           {name:'Employers_WC_Quote.pdf', kind:'Quote', ok:true, note:'Reviewed — no restrictive endorsements', when:'2026-07-28'}],
     comms:[{who:'Sam Okafor · Employers', dir:'in', channel:'Email', txt:'Binder issued effective Nov 1. Policy documents follow within 10 days.', when:'2026-08-06'},
            {who:'Priya Shah', dir:'out', channel:'Email', txt:'Accepted terms and returned the signed binder request.', when:'2026-08-05'}]},

    {id:'md-2', carrier:'Coalition', market:'Admitted', channel:'via Rate', submissionNo:'SUB-881044',
     lob:'Cyber', stage:'bound', submittedOn:'2026-07-14', lastActivity:'2026-08-06', slaDays:5,
     premium:6850, commission:15, limits:'$2M aggregate', deductible:'$10,000', validUntil:'2026-11-01',
     uw:{name:'Ivy Nakamura', email:'inakamura@coalitioninc.com', phone:'(415) 855-4700'},
     subjectivities:[{label:'Security scan — passed', status:'cleared', on:'2026-07-16'},
                     {label:'MFA attestation', status:'cleared', on:'2026-07-20'}],
     flags:[],
     docs:[{name:'Coalition_Cyber_Binder.pdf', kind:'Binder', ok:true, note:'Includes ransomware coverage at full limit', when:'2026-08-06'}],
     comms:[{who:'Ivy Nakamura · Coalition', dir:'in', channel:'Portal', txt:'Bound at $6,850 with full-limit ransomware. Security score improved to A-.', when:'2026-08-06'}]},

    {id:'md-3', carrier:'AmTrust', market:'Admitted', channel:'Direct appointment', submissionNo:'SUB-881019',
     lob:'Workers Compensation', stage:'ntu', submittedOn:'2026-07-11', lastActivity:'2026-08-05', slaDays:5,
     premium:41900, commission:10, limits:'Statutory / $1M EL', deductible:'—', validUntil:'2026-08-30',
     uw:{name:'Priyanka Rao', email:'prao@amtrustgroup.com', phone:'(866) 272-9267'},
     subjectivities:[{label:'Signed application', status:'cleared', on:'2026-07-11'}],
     flags:[], docs:[{name:'AmTrust_WC_Quote.pdf', kind:'Quote', ok:true, note:'Competitive but $6,120 above Employers', when:'2026-07-26'}],
     comms:[{who:'Priya Shah', dir:'out', channel:'Email', txt:'Thanks for the terms — client is placing with the incumbent this year.', when:'2026-08-05'}]},
  ],
  automations:[
    {t:'Policy-delivery checklist', s:'Opens a CSR task set the moment a submission is marked bound', on:true, fired:'Fired Aug 6 · 4 tasks created'},
    {t:'Certificate auto-issue', s:'Generates certificates for the holders on file', on:true, fired:'Fired Aug 7 · 6 certificates'},
    {t:'Renewal review at 120 days', s:'Reopens the opportunity ahead of the next term', on:true, fired:'Next fires Jul 4, 2027'},
  ],
  clientDocs:[
    {t:'Signed binder request', s:'Received Aug 5', status:'done'},
    {t:'Payroll verification', s:'Received Jul 24', status:'done'},
    {t:'MFA attestation', s:'Received Jul 20', status:'done'},
  ],
  clientMsgs:[
    {who:'Priya Shah · Sunrise Insurance', txt:'You are bound for Nov 1 on both the workers comp and the cyber policy — combined, $4,170 under what you paid last year. Certificates are on their way.', when:'2026-08-07'},
    {who:'Dr. Amara Osei', txt:'Fantastic, thank you both. Please send a certificate to the new Elm Street landlord as well.', when:'2026-08-07'},
  ],
},

{
  id:'ironclad', account:'Ironclad Fabrication', dba:'Ironclad',
  oppName:'Property + Inland Marine — 2026 Renewal', lobs:['Commercial Property','Inland Marine'],
  lobShort:'PR', type:'Renewal', oppStage:'proposal', tier:'Level 2',
  effective:'2026-08-20', expiring:'2026-08-20', expiringPremium:128400, targetPremium:132000,
  producer:'Tomas Ibarra', csr:'Marcus Lin', marketer:'Devon Cole',
  contact:{name:'Hal Brenner', title:'General Manager', email:'hbrenner@ironcladfab.com', phone:'(555) 209-4478'},
  quoteRef:'QT-IF-7712', health:'risk',
  tracker:{shared:true, views:2, lastView:'2026-08-07', proposalSent:'2026-08-07', proposalOpened:false},
  nextAction:{label:'Client has not opened the proposal — call Hal directly', owner:'Tomas Ibarra', due:'2026-08-09'},
  note:'Proposal sent Aug 7 and still unopened with 10 days to the effective date. Best available terms are 7.4% above expiring because CNA declined on hot-work controls.',
  submissions:[
    {id:'if-1', carrier:'Philadelphia Insurance', market:'Admitted', channel:'Direct appointment', submissionNo:'SUB-889210',
     lob:'Commercial Property', stage:'quoted', submittedOn:'2026-07-18', lastActivity:'2026-08-06', slaDays:5,
     premium:137900, commission:12, limits:'$8.6M blanket', deductible:'$25,000', validUntil:'2026-08-25',
     uw:{name:'Owen Marsh', email:'omarsh@phly.com', phone:'(800) 873-4552'},
     subjectivities:[{label:'Hot work permit program', status:'cleared', on:'2026-08-04'},
                     {label:'Signed application', status:'cleared', on:'2026-07-18'},
                     {label:'Sprinkler inspection report', status:'open', due:'2026-08-15', owner:'Hal Brenner (client)'}],
     flags:[{kind:'expiry', label:'Quote expires in 15 days'}],
     docs:[{name:'PHLY_Property_Quote.pdf', kind:'Quote', ok:true, note:'Hot work sublimit acceptable at $2M', when:'2026-08-06'}],
     comms:[{who:'Owen Marsh · Philadelphia', dir:'in', channel:'Email', txt:'Terms at $137,900 once the hot work program was documented. Sprinkler report still needed to bind.', when:'2026-08-06'}]},

    {id:'if-2', carrier:'Chubb', market:'Admitted', channel:'Direct appointment', submissionNo:'SUB-889188',
     lob:'Commercial Property', stage:'ntu', submittedOn:'2026-07-16', lastActivity:'2026-08-07', slaDays:5,
     premium:141200, commission:12, limits:'$8.6M blanket', deductible:'$25,000', validUntil:'2026-08-24',
     uw:{name:'Diane Halloway', email:'dhalloway@chubb.com', phone:'(908) 903-2000'},
     subjectivities:[{label:'Signed application', status:'cleared', on:'2026-07-16'}],
     flags:[], docs:[{name:'Chubb_Property_Quote.pdf', kind:'Quote', ok:true, note:'$3,300 above Philadelphia on identical limits', when:'2026-07-30'}],
     comms:[{who:'Marcus Lin', dir:'out', channel:'Email', txt:'Holding as the backup option — Philadelphia is leading on price and terms.', when:'2026-08-07'}]},

    {id:'if-3', carrier:'CNA', market:'Admitted', channel:'Direct appointment', submissionNo:'SUB-889195',
     lob:'Commercial Property', stage:'declined', submittedOn:'2026-07-17', lastActivity:'2026-07-31', slaDays:5,
     premium:null, commission:null, limits:'—', deductible:'—', validUntil:null,
     declineReason:'Welding operations without a documented hot work permit program at time of submission.',
     uw:{name:'Errol Mendez', email:'errol.mendez@cna.com', phone:'(312) 822-5000'},
     subjectivities:[], flags:[], docs:[],
     comms:[{who:'Errol Mendez · CNA', dir:'in', channel:'Email', txt:'Declining — hot work controls were not documented at submission.', when:'2026-07-31'}]},

    {id:'if-4', carrier:'AmTrust', market:'Admitted', channel:'via Rate', submissionNo:'SUB-889221',
     lob:'Inland Marine', stage:'info', submittedOn:'2026-07-28', lastActivity:'2026-08-05', slaDays:5,
     premium:null, commission:15, limits:'$600K equipment', deductible:'$5,000', validUntil:null,
     uw:{name:'Priyanka Rao', email:'prao@amtrustgroup.com', phone:'(866) 272-9267'},
     subjectivities:[{label:'Scheduled equipment list with serials', status:'open', due:'2026-08-12', owner:'Hal Brenner (client)'}],
     flags:[{kind:'client', label:'Waiting on client · 1 item'}],
     docs:[], comms:[{who:'Priyanka Rao · AmTrust', dir:'in', channel:'Email', txt:'Need the scheduled equipment list with serial numbers to rate the inland marine.', when:'2026-08-05'}]},
  ],
  automations:[
    {t:'Proposal-open tracking', s:'Watches whether the insured opened the proposal link', on:true, fired:'No open recorded since Aug 7'},
    {t:'Unopened proposal escalation', s:'Creates a call task for the producer after 48 hours', on:true, fired:'Escalated Aug 9 → task created'},
    {t:'Quote expiry warning at 7 days', s:'Philadelphia terms lapse Aug 25', on:true, fired:'Fires Aug 18'},
  ],
  clientDocs:[
    {t:'Sprinkler inspection report', s:'Requested Aug 6 · required to bind', status:'open'},
    {t:'Scheduled equipment list', s:'Requested Aug 5 · blocks inland marine quote', status:'open'},
    {t:'Hot work permit program', s:'Received Aug 4 — reversed one decline', status:'done'},
  ],
  clientMsgs:[
    {who:'Tomas Ibarra · Sunrise Insurance', txt:'Your proposal is ready. Premium is up 7.4% — that is entirely driven by the welding exposure, and documenting your hot work program already saved a decline. Let us get 20 minutes on the calendar.', when:'2026-08-07'},
  ],
},

{
  id:'abyss', account:'Finn Abyss', dba:null,
  oppName:'Personal Auto — 2026 Renewal', lobs:['Personal Auto'],
  lobShort:'PA', type:'Renewal', oppStage:'proposal', tier:'Level 2',
  effective:'2026-10-01', expiring:'2026-10-01', expiringPremium:1180, targetPremium:1050,
  producer:'Rachel Vance', csr:'Priya Shah', marketer:'—',
  contact:{name:'Finn Abyss', title:'Insured', email:'finn.abyss@example.com', phone:'(555) 884-2210'},
  quoteRef:'QT-PA-1001', health:'on',
  tracker:{shared:true, views:4, lastView:'2026-08-09', proposalSent:'2026-08-08', proposalOpened:true},
  nextAction:{label:'Follow up on the proposal — client viewed it twice', owner:'Priya Shah', due:'2026-08-12'},
  note:'Personal lines renewal review. Progressive came back $180 under expiring; Safeco still rating a multi-policy option.',
  submissions:[
    {id:'pa-1', carrier:'Progressive', market:'Admitted', channel:'via Rate', submissionNo:'SUB-890455',
     lob:'Personal Auto', stage:'quoted', submittedOn:'2026-08-01', lastActivity:'2026-08-08', slaDays:3,
     premium:1000, commission:12, limits:'100/300/100', deductible:'$500 comp / $500 coll', validUntil:'2026-10-01', incumbent:true,
     uw:{name:'Automated rating', email:'—', phone:'(800) 776-4737'},
     subjectivities:[{label:'Signed application', status:'cleared', on:'2026-08-01'},
                     {label:'Photo inspection — 2024 vehicle', status:'cleared', on:'2026-08-06'}],
     flags:[],
     docs:[{name:'Progressive_PA_Quote.pdf', kind:'Quote', ok:true, note:'Multi-car and safe-driver discounts applied', when:'2026-08-08'}],
     comms:[{who:'Progressive', dir:'in', channel:'Portal', txt:'Renewal rated at $1,000 annual with the safe-driver discount retained.', when:'2026-08-08'}]},

    {id:'pa-2', carrier:'Safeco', market:'Admitted', channel:'via Rate', submissionNo:'SUB-890461',
     lob:'Personal Auto', stage:'review', submittedOn:'2026-08-05', lastActivity:'2026-08-07', slaDays:3,
     premium:null, commission:13, limits:'100/300/100', deductible:'—', validUntil:null,
     uw:{name:'Personal lines desk', email:'plservice@safeco.com', phone:'(800) 332-3226'},
     subjectivities:[{label:'Home policy details for the bundle credit', status:'open', due:'2026-08-13', owner:'Priya Shah'}],
     flags:[], docs:[],
     comms:[{who:'Safeco personal lines', dir:'in', channel:'Portal', txt:'Rating a bundled option — send the homeowners details to apply the multi-policy credit.', when:'2026-08-07'}]},
  ],
  automations:[
    {t:'Renewal proposal auto-send', s:'Emails the proposal the moment the first quote clears review', on:true, fired:'Fired Aug 8'},
    {t:'Proposal-open tracking', s:'Watches whether the insured opened the proposal link', on:true, fired:'Opened 2× · last Aug 9'},
    {t:'Renewal review at 60 days', s:'Reopened this opportunity on Aug 1', on:true, fired:'Fired Aug 1'},
  ],
  clientDocs:[
    {t:'Homeowners policy details', s:'Requested Aug 7 · unlocks a bundle credit', status:'open'},
    {t:'Photo inspection — 2024 vehicle', s:'Received Aug 6', status:'done'},
  ],
  clientMsgs:[
    {who:'Priya Shah · Sunrise Insurance', txt:'Good news — your renewal came back $180 lower than last year. I am also checking a bundled option that could save a little more.', when:'2026-08-08'},
  ],
}
];
